import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Megaphone, Search, Mail, Palette, TrendingUp, Share2, PenTool, Globe, Star, Shield, Zap, Brain, Video, Phone, MessageSquare, FileText, ArrowRight, CheckCircle2, Clock, LineChart, Database, RefreshCw, Layers, Target } from 'lucide-react';
export function AutopilotMarketing() {
  // Animation variants
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 30
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <main className="min-h-screen bg-white text-gray-900 font-inter selection:bg-purple/30 selection:text-white pt-20" data-id="element-1013" data-mp="2c9p4ng71t4ra">
      {/* 1. HERO SECTION */}
      <section className="relative pt-24 pb-32 px-6 bg-hero-gradient overflow-hidden min-h-[85vh] flex items-center" data-id="element-1014" data-mp="2h8ihd6eigrqe">
        {/* Ambient Glows */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-40 pointer-events-none" data-id="element-1015" data-mp="2ggzazviv0ewy" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-30 pointer-events-none" data-id="element-1016" data-mp="2ufp7pa1rocks" />

        {/* Floating Icons Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1017" data-mp="21sl5tdxz0ed8">
          {[{
          Icon: Megaphone,
          top: '20%',
          left: '10%',
          delay: 0
        }, {
          Icon: Search,
          top: '15%',
          left: '80%',
          delay: 1
        }, {
          Icon: Mail,
          top: '70%',
          left: '15%',
          delay: 2
        }, {
          Icon: Palette,
          top: '60%',
          left: '85%',
          delay: 0.5
        }, {
          Icon: TrendingUp,
          top: '40%',
          left: '90%',
          delay: 1.5
        }, {
          Icon: Share2,
          top: '80%',
          left: '50%',
          delay: 2.5
        }].map((item, i) => <motion.div key={i} className="absolute text-white/10" style={{
          top: item.top,
          left: item.left
        }} animate={{
          y: [0, -30, 0],
          rotate: [0, 15, -15, 0],
          scale: [1, 1.1, 1]
        }} transition={{
          duration: 8,
          repeat: Infinity,
          delay: item.delay,
          ease: 'easeInOut'
        }} data-id="element-1018" data-mp="3rxg4fgduaiaq" data-mpi={i}>
              <item.Icon size={64} data-id="element-1019" data-mp="oe3lsngiezjy" />
            </motion.div>)}
        </div>

        <div className="inner-container relative z-10 text-center max-w-5xl mx-auto" data-id="element-1020" data-mp="yjcxvnvbnnrn">
          <motion.div initial="hidden" animate="show" variants={staggerContainer} data-id="element-1021" data-mp="tk0ejd3nsytt">
            <motion.span variants={fadeUp} className="eyebrow mx-auto" data-id="element-1022" data-mp="oryuv645w5tr">
              Self-Running Marketing
            </motion.span>
            <motion.h1 variants={fadeUp} className="font-poppins font-extrabold text-5xl md:text-7xl leading-[1.1] mb-6 text-white" data-id="element-1023" data-mp="kpc99my33h50">
              Your Entire Marketing Department. <br data-id="element-1024" data-mp="avlnwqx97mtj" />
              <span className="text-gradient" data-id="element-1025" data-mp="5twax5qh0z2t">Running Itself.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-xl md:text-2xl text-steel mb-12 leading-relaxed max-w-3xl mx-auto" data-id="element-1026" data-mp="1skiksg7pzqx">
              Replace expensive agencies, unreliable freelancers, and scattered
              tools with one AI intelligence that knows your brand inside and
              out.
            </motion.p>

            {/* Stats Bar */}
            <motion.div variants={fadeUp} className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 max-w-4xl mx-auto bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm shadow-2xl" data-id="element-1027" data-mp="288v4hlezp76t">
              <div data-id="element-1028" data-mp="1y9nrizc63ek8">
                <div className="text-3xl font-bold text-white font-poppins mb-1" data-id="element-1029" data-mp="1y947nd2yig3k">
                  8
                </div>
                <div className="text-xs text-steel uppercase tracking-wider font-medium" data-id="element-1030" data-mp="1kh7nbv7j5ahq">
                  Disciplines
                </div>
              </div>
              <div data-id="element-1031" data-mp="1amfn0jmkc9j9">
                <div className="text-3xl font-bold text-white font-poppins mb-1" data-id="element-1032" data-mp="1amxt07z1224d">
                  24/7
                </div>
                <div className="text-xs text-steel uppercase tracking-wider font-medium" data-id="element-1033" data-mp="11kg4tnztazeq">
                  Execution
                </div>
              </div>
              <div data-id="element-1034" data-mp="16izsbsu20j97">
                <div className="text-3xl font-bold text-white font-poppins mb-1" data-id="element-1035" data-mp="16llybo1aqqpb">
                  1
                </div>
                <div className="text-xs text-steel uppercase tracking-wider font-medium" data-id="element-1036" data-mp="hympico4rq91">
                  Brand Voice
                </div>
              </div>
              <div data-id="element-1037" data-mp="vzyzm7gm0xn0">
                <div className="text-3xl font-bold text-white font-poppins mb-1" data-id="element-1038" data-mp="vw2urkuoxdz7">
                  Zero
                </div>
                <div className="text-xs text-steel uppercase tracking-wider font-medium" data-id="element-1039" data-mp="15wbsj34bz7gp">
                  Freelancers
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 2. THE PERSONA ENGINE SECTION */}
      <section className="py-24 px-6 bg-white relative overflow-hidden" data-id="element-1040" data-mp="1anbredx0fzlu">
        <div className="inner-container max-w-6xl mx-auto" data-id="element-1041" data-mp="1ao3m7mis9tb2">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1042" data-mp="1ombirtbwqslp">
            <span className="text-purple text-sm font-poppins font-bold uppercase tracking-widest block mb-4" data-id="element-1043" data-mp="1jo07de5ysa1d">
              The Persona Engine
            </span>
            <h2 className="font-poppins font-extrabold text-4xl md:text-5xl leading-tight text-gray-900 mb-6" data-id="element-1044" data-mp="hc5bsjrhuirr">
              It Starts With Who You Are.
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-1045" data-mp="qb0jy1e3m2xp">
              Great marketing isn't just about posting—it's about capturing the
              exact essence, tone, and authority of your company. We build a
              deep, self-evolving brand persona by tapping into the data you
              already have.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-12 items-center" data-id="element-1046" data-mp="151zjkmwyhh55">
            {/* Visual Diagram */}
            <div className="lg:col-span-7 relative" data-id="element-1047" data-mp="1e0uqk17tqv9i">
              <div className="relative w-full aspect-square max-w-lg mx-auto" data-id="element-1048" data-mp="1e0b9emq00awj">
                {/* Center Brain */}
                <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-dark-bg rounded-full border-4 border-purple shadow-[0_0_40px_rgba(124,58,237,0.3)] flex items-center justify-center z-20" animate={{
                boxShadow: ['0 0 40px rgba(124,58,237,0.3)', '0 0 80px rgba(124,58,237,0.6)', '0 0 40px rgba(124,58,237,0.3)']
              }} transition={{
                duration: 4,
                repeat: Infinity
              }} data-id="element-1049" data-mp="3bsfmwdkeex07">
                  <Brain size={48} className="text-white" data-id="element-1050" data-mp="3m9j7m0yl5edp" />
                </motion.div>

                {/* Connecting Lines (SVG) */}
                <svg className="absolute inset-0 w-full h-full z-0" viewBox="0 0 400 400" data-id="element-1051" data-mp="3i8za2wa3o8r1">
                  <motion.circle cx="200" cy="200" r="120" fill="none" stroke="#e5e7eb" strokeWidth="2" strokeDasharray="8 8" data-id="element-1052" data-mp="26s90r4adr0t7" />
                  {/* Animated data particles */}
                  <motion.circle cx="200" cy="80" r="4" fill="#7c3aed" animate={{
                  cy: [80, 160]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity
                }} data-id="element-1053" data-mp="9smtx4gkuhso" />
                  <motion.circle cx="80" cy="200" r="4" fill="#0785B7" animate={{
                  cx: [80, 160]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: 0.5
                }} data-id="element-1054" data-mp="tej8fdjeazkx" />
                  <motion.circle cx="320" cy="200" r="4" fill="#7c3aed" animate={{
                  cx: [320, 240]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: 1
                }} data-id="element-1055" data-mp="1c7de1gv4pfch" />
                  <motion.circle cx="115" cy="315" r="4" fill="#0785B7" animate={{
                  cx: [115, 170],
                  cy: [315, 230]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: 0.2
                }} data-id="element-1056" data-mp="2dwldxc0y1gad" />
                  <motion.circle cx="285" cy="315" r="4" fill="#7c3aed" animate={{
                  cx: [285, 230],
                  cy: [315, 230]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: 0.8
                }} data-id="element-1057" data-mp="29do1l7a518d" />
                </svg>

                {/* Nodes */}
                <div className="absolute top-[10%] left-1/2 -translate-x-1/2 w-16 h-16 bg-dark-bg rounded-2xl border border-white/10 flex items-center justify-center z-10 shadow-xl" data-id="element-1058" data-mp="2urbpzibsay4g">
                  <Video size={24} className="text-teal" data-id="element-1059" data-mp="2ktfkb61m8vdq" />
                </div>
                <div className="absolute top-1/2 left-[10%] -translate-y-1/2 w-16 h-16 bg-dark-bg rounded-2xl border border-white/10 flex items-center justify-center z-10 shadow-xl" data-id="element-1060" data-mp="2kvi95mr18igv">
                  <Mail size={24} className="text-purple" data-id="element-1061" data-mp="j1j0781dud7c" />
                </div>
                <div className="absolute top-1/2 right-[10%] -translate-y-1/2 w-16 h-16 bg-dark-bg rounded-2xl border border-white/10 flex items-center justify-center z-10 shadow-xl" data-id="element-1062" data-mp="f1sbd9vf275g">
                  <Phone size={24} className="text-teal" data-id="element-1063" data-mp="21wdo137z9jo9" />
                </div>
                <div className="absolute bottom-[15%] left-[20%] w-16 h-16 bg-dark-bg rounded-2xl border border-white/10 flex items-center justify-center z-10 shadow-xl" data-id="element-1064" data-mp="26o5e7cn81ndv">
                  <MessageSquare size={24} className="text-purple" data-id="element-1065" data-mp="2arbkule8xgkv" />
                </div>
                <div className="absolute bottom-[15%] right-[20%] w-16 h-16 bg-dark-bg rounded-2xl border border-white/10 flex items-center justify-center z-10 shadow-xl" data-id="element-1066" data-mp="1e1azyxgzzk8u">
                  <Globe size={24} className="text-teal" data-id="element-1067" data-mp="1mz4vw7auobvh" />
                </div>
              </div>
            </div>

            {/* Content Cards */}
            <div className="lg:col-span-5 space-y-6" data-id="element-1068" data-mp="2yf35cc9xwzx8">
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} className="bg-dark-bg border border-white/10 rounded-2xl p-8 shadow-xl" data-id="element-1069" data-mp="2yejnswo7cdjq">
                <div className="w-12 h-12 bg-teal/10 rounded-xl flex items-center justify-center text-teal mb-6" data-id="element-1070" data-mp="1d9b55bfjq3yq">
                  <Zap size={24} data-id="element-1071" data-mp="1896rdt60vnc7" />
                </div>
                <h3 className="font-poppins font-bold text-xl text-white mb-3" data-id="element-1072" data-mp="km6yz34pvjld">
                  Built in Days, Not Weeks
                </h3>
                <p className="text-steel leading-relaxed" data-id="element-1073" data-mp="fkhlzhxxovun">
                  Most agencies take 6-8 weeks and endless questionnaires to
                  build a brand voice. We do it in days because we tap directly
                  into your Zoom meetings, emails, and Slack history.
                </p>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.2
            }} className="bg-dark-bg border border-white/10 rounded-2xl p-8 shadow-xl relative overflow-hidden" data-id="element-1074" data-mp="3t20fhhz1sv2f">
                <div className="absolute top-0 right-0 w-32 h-32 bg-purple/10 rounded-full blur-2xl pointer-events-none" data-id="element-1075" data-mp="2raqqm03qacdy" />
                <div className="w-12 h-12 bg-purple/10 rounded-xl flex items-center justify-center text-purple mb-6 relative z-10" data-id="element-1076" data-mp="2w7zd2cvwt2l0">
                  <Shield size={24} data-id="element-1077" data-mp="2n82ijumu99i1" />
                </div>
                <h3 className="font-poppins font-bold text-xl text-white mb-3 relative z-10" data-id="element-1078" data-mp="2n6j32690b2hb">
                  100% Private & Local
                </h3>
                <p className="text-steel leading-relaxed relative z-10" data-id="element-1079" data-mp="1btglb7v686vk">
                  Your data never leaves your servers. We process everything
                  locally to build your persona. No risk of training public AI
                  models. Complete confidentiality.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. THE MARKETING WHEEL SECTION */}
      <section className="py-32 px-6 bg-white relative overflow-hidden" data-id="element-1080" data-mp="1dcgtdwd2ehzq">
        <div className="inner-container max-w-7xl mx-auto" data-id="element-1081" data-mp="2a1oifecw798e">
          <div className="text-center max-w-3xl mx-auto mb-24" data-id="element-1082" data-mp="29z2cfj5nh1sa">
            <h2 className="font-poppins font-extrabold text-4xl md:text-5xl leading-tight text-gray-900 mb-6" data-id="element-1083" data-mp="253mx0mrzuxtr">
              Every Piece of the Puzzle.{' '}
              <span className="text-gradient" data-id="element-1084" data-mp="1w1x6rk9um4xk">Handled.</span>
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1085" data-mp="1w3zv7zvcrq06">
              Marketing isn't just one thing—it's an ecosystem. MISTR seamlessly
              connects and executes across every discipline simultaneously.
            </p>
          </div>

          {/* Radial Wheel Graphic */}
          <div className="relative w-[320px] h-[320px] md:w-[600px] md:h-[600px] mx-auto my-12" data-id="element-1086" data-mp="2wboigcb980yz">
            {/* Center Core */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 md:w-48 md:h-48 bg-dark-bg rounded-full border border-white/10 shadow-[0_0_60px_rgba(7,133,183,0.2)] flex flex-col items-center justify-center z-20" data-id="element-1087" data-mp="35ajs5ydibt71">
              <Brain size={40} className="text-teal mb-2" data-id="element-1088" data-mp="3ezjrxjzu2sma" />
              <span className="font-poppins font-bold text-white text-sm md:text-base text-center leading-tight" data-id="element-1089" data-mp="3jz64rsbr26uk">
                MISTR
                <br data-id="element-1090" data-mp="3ozu3z1a4ttzv" />
                Persona Core
              </span>
            </div>

            {/* Connecting Rings */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full border border-gray-200 rounded-full z-0" data-id="element-1091" data-mp="3sy08e2nhzxhm" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-[70%] border border-gray-200 rounded-full z-0" data-id="element-1092" data-mp="1yljtyekjtd1o" />

            {/* The 8 Slices/Nodes */}
            {[{
            icon: Search,
            label: 'SEO',
            desc: 'Rankings & Outreach'
          }, {
            icon: TrendingUp,
            label: 'PPC',
            desc: 'Paid Ads'
          }, {
            icon: Share2,
            label: 'Social',
            desc: 'Community'
          }, {
            icon: PenTool,
            label: 'Content',
            desc: 'Blogs & Copy'
          }, {
            icon: Mail,
            label: 'Email',
            desc: 'Newsletters'
          }, {
            icon: Globe,
            label: 'Web Design',
            desc: 'UX/UI'
          }, {
            icon: Palette,
            label: 'Graphics',
            desc: 'Visuals'
          }, {
            icon: Star,
            label: 'Reputation',
            desc: 'Reviews'
          }].map((item, i) => {
            const angle = i * 360 / 8;
            // Calculate position using CSS transforms
            return <motion.div key={i} className="absolute top-1/2 left-1/2 w-20 h-20 md:w-32 md:h-32 -ml-10 -mt-10 md:-ml-16 md:-mt-16 z-10 group" style={{
              transform: `rotate(${angle}deg) translateY(-160px) rotate(-${angle}deg)`
            }}
            // Responsive adjustment for mobile vs desktop radius
            initial={{
              opacity: 0,
              scale: 0
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1,
              type: 'spring'
            }} data-id="element-1093" data-mp="h3j5lxp3q0r7" data-mpi={i}>
                  {/* Mobile uses a smaller radius via CSS media queries in a real app, but here we use a clever trick: we scale the whole container down on mobile, so the math holds */}
                  <div className="w-full h-full bg-dark-bg border border-white/10 rounded-2xl flex flex-col items-center justify-center p-2 hover:bg-navy-dark hover:border-purple/50 transition-all cursor-default shadow-lg group-hover:shadow-[0_0_30px_rgba(124,58,237,0.3)]" data-id="element-1094" data-mp="3n33t1b3yin6u">
                    <item.icon size={24} className="text-teal mb-2 group-hover:text-purple transition-colors md:w-8 md:h-8" data-id="element-1095" data-mp="38co69rvw776k" />
                    <span className="font-poppins font-bold text-white text-[10px] md:text-sm text-center" data-id="element-1096" data-mp="2za57pjz53gq4">
                      {item.label}
                    </span>
                    <span className="hidden md:block text-[10px] text-steel/70 text-center mt-1 leading-tight" data-id="element-1097" data-mp="19uuj9iomcryd">
                      {item.desc}
                    </span>
                  </div>
                </motion.div>;
          })}

            {/* CSS override for mobile radius scaling */}
            <style dangerouslySetInnerHTML={{
            __html: `
              @media (max-width: 768px) {
                .group { transform: rotate(var(--angle)) translateY(-130px) rotate(calc(var(--angle) * -1)) !important; }
              }
            `
          }} data-id="element-1098" data-mp="1nuc81y1x057q" />
          </div>
        </div>
      </section>

      {/* 4. DEEP DIVE SECTIONS */}

      {/* A. SEO */}
      <section className="py-24 px-6 bg-white relative overflow-hidden border-t border-gray-100" data-id="element-1099" data-mp="14w020l6avbgd">
        <div className="inner-container max-w-6xl mx-auto" data-id="element-1100" data-mp="7f2nazl893vs">
          <div className="flex flex-col md:flex-row items-center gap-16" data-id="element-1101" data-mp="cenprjzlvud9">
            <div className="flex-1" data-id="element-1102" data-mp="ccko22p3ccsp">
              <div className="w-16 h-16 bg-teal/10 rounded-2xl flex items-center justify-center text-teal mb-6" data-id="element-1103" data-mp="3fzn0nchsa8sf">
                <Search size={32} data-id="element-1104" data-mp="3jzfdw2i4h344" />
              </div>
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-6" data-id="element-1105" data-mp="3ozjvjvuro3vx">
                Search Engine Dominance
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-1106" data-mp="3txbnuqzbps0n">
                We don't just optimize meta tags. MISTR handles the entire SEO
                ecosystem, from technical audits to natural, human-like
                relationship building.
              </p>
              <ul className="space-y-4" data-id="element-1107" data-mp="368savy5bwr28">
                {['On-page optimization: meta tags, structure, and keyword targeting', 'Natural backlink outreach: Not spammy software blasts. Human-like, personalized outreach that builds real relationships.', 'Technical SEO: site speed, schema markup, and crawlability', 'Continuous keyword research and content gap analysis'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-1108" data-mp="260c6vh0homx7" data-mpi={i}>
                    <CheckCircle2 size={20} className="text-teal shrink-0 mt-1" data-id="element-1109" data-mp="211sjdc68z32q" />
                    <span data-id="element-1110" data-mp="1w1we79ltd5ll">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="flex-1 w-full" data-id="element-1111" data-mp="1s2nja1f1f1og">
              <div className="bg-dark-bg border border-white/10 rounded-2xl p-8 shadow-2xl relative" data-id="element-1112" data-mp="1nanqqu1arb1w">
                <div className="absolute -top-4 -right-4 bg-purple text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg" data-id="element-1113" data-mp="1ib1cqidn9qs1">
                  Human-Like Outreach
                </div>
                <div className="space-y-4" data-id="element-1114" data-mp="18d3o3trvhryw">
                  <div className="h-4 w-1/3 bg-white/10 rounded animate-pulse" data-id="element-1115" data-mp="18btq14l0sjs9" />
                  <div className="h-4 w-full bg-white/5 rounded" data-id="element-1116" data-mp="14b9to389tk76" />
                  <div className="h-4 w-5/6 bg-white/5 rounded" data-id="element-1117" data-mp="tqegf4ngw8hf" />
                  <div className="p-4 bg-dark-bg border border-white/10 rounded-lg mt-6" data-id="element-1118" data-mp="tp3df71uj4rd">
                    <p className="text-sm text-steel/70 mb-2" data-id="element-1119" data-mp="yr2j4nl26drh">
                      Drafted Email to Industry Blog:
                    </p>
                    <p className="text-sm text-white font-medium" data-id="element-1120" data-mp="pota625fusg7">
                      "Hey Sarah, loved your recent piece on AI automation. I
                      noticed a small broken link in paragraph 3. Also, we just
                      published a data-backed study on this exact topic that
                      your readers might find valuable..."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* B. PPC */}
      <section className="py-24 px-6 bg-white relative overflow-hidden" data-id="element-1121" data-mp="1wczsnvhgn8ra">
        <div className="inner-container max-w-6xl mx-auto" data-id="element-1122" data-mp="213pz7c1mhhnk">
          <div className="flex flex-col md:flex-row-reverse items-center gap-16" data-id="element-1123" data-mp="215atd0k0yykw">
            <div className="flex-1" data-id="element-1124" data-mp="2ahgwp17ucffc">
              <div className="w-16 h-16 bg-purple/10 rounded-2xl flex items-center justify-center text-purple mb-6" data-id="element-1125" data-mp="2agfco78ukilh">
                <TrendingUp size={32} data-id="element-1126" data-mp="2k8bkia0qjn2w" />
              </div>
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-6" data-id="element-1127" data-mp="2k7tfanw3hyiu">
                Precision Paid Ads
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-1128" data-mp="1rje2m80frsao">
                Stop wasting budget on campaigns you forgot to monitor. MISTR
                watches your ad spend 24/7, optimizing for ROAS in real-time.
              </p>
              <ul className="space-y-4" data-id="element-1129" data-mp="21jere9h2r0m9">
                {['Campaign creation across Google Ads and Meta', 'Automatic A/B testing of ad copy and creatives', 'Real-time budget optimization and ROAS tracking', 'Automatic pausing of underperforming ad sets'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-1130" data-mp="1hi3p13hb8pqv" data-mpi={i}>
                    <CheckCircle2 size={20} className="text-purple shrink-0 mt-1" data-id="element-1131" data-mp="1mfn0jvmmiof4" />
                    <span data-id="element-1132" data-mp="1rdyas7gtz0xv">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="flex-1 w-full" data-id="element-1133" data-mp="1veqlz699jfws">
              <div className="bg-dark-bg border border-white/10 rounded-2xl p-8 shadow-2xl" data-id="element-1134" data-mp="20gpqwkknikvu">
                <div className="flex items-center justify-between mb-6 border-b border-white/10 pb-4" data-id="element-1135" data-mp="25ehkdj0y2f25">
                  <span className="text-white font-semibold" data-id="element-1136" data-mp="2a5hc89v7i6xs">
                    Campaign: Q4 Retargeting
                  </span>
                  <span className="text-teal font-bold" data-id="element-1137" data-mp="2f4ausgpcoo4t">+24% ROAS</span>
                </div>
                <div className="space-y-4" data-id="element-1138" data-mp="2j80eosv2yhji">
                  <div className="flex justify-between items-center bg-white/5 p-3 rounded-lg" data-id="element-1139" data-mp="2o5ig7y6rpuhh">
                    <span className="text-steel text-sm" data-id="element-1140" data-mp="ci5hprmz68xp">
                      Ad Variant A (Paused)
                    </span>
                    <span className="text-red-400 text-sm font-medium" data-id="element-1141" data-mp="7issx1cjmrsh">
                      $45 / CPA
                    </span>
                  </div>
                  <div className="flex justify-between items-center bg-teal/10 border border-teal/30 p-3 rounded-lg" data-id="element-1142" data-mp="3jl5n9bx6xig">
                    <span className="text-white text-sm font-medium" data-id="element-1143" data-mp="v8n2na1kg08i">
                      Ad Variant B (Scaling)
                    </span>
                    <span className="text-teal text-sm font-bold" data-id="element-1144" data-mp="r8cpndfcw9k5">
                      $12 / CPA
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* C. Social Media */}
      <section className="py-24 px-6 bg-white relative overflow-hidden border-t border-gray-100" data-id="element-1145" data-mp="1ewvwrpmo6gan">
        <div className="inner-container max-w-6xl mx-auto" data-id="element-1146" data-mp="1a5nm6q9lsovw">
          <div className="flex flex-col md:flex-row items-center gap-16" data-id="element-1147" data-mp="1a42qgxbjyzwg">
            <div className="flex-1" data-id="element-1148" data-mp="1t2xw4n76v9xm">
              <div className="w-16 h-16 bg-teal/10 rounded-2xl flex items-center justify-center text-teal mb-6" data-id="element-1149" data-mp="1y0zet70pd78k">
                <Share2 size={32} data-id="element-1150" data-mp="1kadvjh7c6va5" />
              </div>
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-6" data-id="element-1151" data-mp="1k7rn7fcmgbqv">
                Omnipresent Social
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-1152" data-mp="1abeuokstc3xp">
                Be everywhere your audience is, without spending your life on
                social media. MISTR researches, creates, and engages in your
                exact brand voice.
              </p>
              <ul className="space-y-4" data-id="element-1153" data-mp="15cbnatpd1lmk">
                {['Platform research and audience analysis', 'Content creation perfectly tuned to your brand voice', 'Automated scheduling and posting across all platforms', 'Performance tracking — doubling down on what works', 'Trend monitoring and real-time engagement'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-1154" data-mp="k2z1f17s17l9" data-mpi={i}>
                    <CheckCircle2 size={20} className="text-teal shrink-0 mt-1" data-id="element-1155" data-mp="xu42m7qsm7xp" />
                    <span data-id="element-1156" data-mp="t09aqh11amnj">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="flex-1 w-full grid grid-cols-2 gap-4" data-id="element-1157" data-mp="12s5loso7y796">
              <div className="bg-dark-bg border border-white/10 rounded-xl p-6 flex flex-col items-center justify-center text-center aspect-square shadow-xl" data-id="element-1158" data-mp="12rvsyxbshlzs">
                <div className="text-3xl font-bold text-white mb-2" data-id="element-1159" data-mp="1bsll9r1ucu8r">14</div>
                <div className="text-sm text-steel" data-id="element-1160" data-mp="3ijb7klbwvh00">Posts This Week</div>
              </div>
              <div className="bg-dark-bg border border-white/10 rounded-xl p-6 flex flex-col items-center justify-center text-center aspect-square shadow-xl" data-id="element-1161" data-mp="3dk8598nbfovq">
                <div className="text-3xl font-bold text-teal mb-2" data-id="element-1162" data-mp="2so0vviqdz4h3">+42%</div>
                <div className="text-sm text-steel" data-id="element-1163" data-mp="2wp2kz6qla05c">Engagement Rate</div>
              </div>
              <div className="col-span-2 bg-dark-bg border border-white/10 rounded-xl p-6 shadow-xl" data-id="element-1164" data-mp="31gkoa1g34cth">
                <div className="flex items-center gap-3 mb-3" data-id="element-1165" data-mp="36g732fbka14e">
                  <div className="w-8 h-8 bg-purple rounded-full flex items-center justify-center" data-id="element-1166" data-mp="29poamym7a455">
                    <Star size={14} className="text-white" data-id="element-1167" data-mp="2dr9vb64z7mpw" />
                  </div>
                  <div data-id="element-1168" data-mp="2dt32qnneyayy">
                    <div className="text-sm font-bold text-white" data-id="element-1169" data-mp="2iqmf1i0jwdo9">
                      Trend Detected
                    </div>
                    <div className="text-xs text-steel/70" data-id="element-1170" data-mp="2nqh3zg0rmt6o">2 hours ago</div>
                  </div>
                </div>
                <p className="text-sm text-steel" data-id="element-1171" data-mp="3u502wmvb0t4a">
                  "Drafted 3 new LinkedIn posts capitalizing on the recent
                  industry news. Ready for your approval."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* D. Content Marketing */}
      <section className="py-24 px-6 bg-white relative overflow-hidden" data-id="element-1172" data-mp="1ik963zbq0sut">
        <div className="inner-container max-w-6xl mx-auto" data-id="element-1173" data-mp="1dpvapwx26lq5">
          <div className="flex flex-col md:flex-row-reverse items-center gap-16" data-id="element-1174" data-mp="18swhdgl7yit0">
            <div className="flex-1" data-id="element-1175" data-mp="14qhxqu9q7f0o">
              <div className="w-16 h-16 bg-purple/10 rounded-2xl flex items-center justify-center text-purple mb-6" data-id="element-1176" data-mp="14rt3v0qn8yuy">
                <PenTool size={32} data-id="element-1177" data-mp="zq3qvfjv2b48" />
              </div>
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-6" data-id="element-1178" data-mp="us26mrap85r6">
                Content That Converts
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-1179" data-mp="pvbpe0n5r446">
                High-quality, SEO-optimized content that actually sounds like
                you wrote it. No generic AI fluff.
              </p>
              <ul className="space-y-4" data-id="element-1180" data-mp="2k5p2i8ji8903">
                {['Blog posts, landing pages, and detailed case studies', 'Written strictly adhering to your persona guidelines', 'SEO-optimized from the very first draft', 'Complete content calendar management and ideation'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-1181" data-mp="3gpfo5iqwwtgi" data-mpi={i}>
                    <CheckCircle2 size={20} className="text-purple shrink-0 mt-1" data-id="element-1182" data-mp="2szlflddmlr22" />
                    <span data-id="element-1183" data-mp="32wzjyem9ype0">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="flex-1 w-full" data-id="element-1184" data-mp="2xxmx3tvelibf">
              <div className="bg-dark-bg border border-white/10 rounded-2xl p-8 shadow-2xl" data-id="element-1185" data-mp="2xwv14hxw9ikm">
                <div className="flex items-center gap-2 mb-6" data-id="element-1186" data-mp="i5gygmaykjdb">
                  <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-1187" data-mp="d8qidyz5lnrw" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-1188" data-mp="krg9ubi0ey2x" />
                  <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-1189" data-mp="kq57mg47pydx" />
                </div>
                <h4 className="text-white font-poppins font-bold text-xl mb-4" data-id="element-1190" data-mp="ps49tmo7up9a">
                  How AI is Changing B2B Sales
                </h4>
                <p className="text-steel text-sm leading-relaxed mb-4" data-id="element-1191" data-mp="fs3lfmbhpiy8">
                  "Most sales teams are drowning in data but starving for
                  context. We've seen this firsthand when we audited over 50
                  enterprise pipelines last quarter..."
                </p>
                <div className="flex gap-2" data-id="element-1192" data-mp="upvzea1e1zab">
                  <span className="text-[10px] uppercase tracking-wider bg-white/10 text-white px-2 py-1 rounded" data-id="element-1193" data-mp="up4h20wsxy1z">
                    SEO Score: 94/100
                  </span>
                  <span className="text-[10px] uppercase tracking-wider bg-teal/20 text-teal px-2 py-1 rounded" data-id="element-1194" data-mp="1mfr2d9i65nhg">
                    Tone Match: 99%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* E. Email Marketing */}
      <section className="py-24 px-6 bg-white relative overflow-hidden border-t border-gray-100" data-id="element-1195" data-mp="1x38l826az4ms">
        <div className="inner-container max-w-6xl mx-auto" data-id="element-1196" data-mp="1s1j8mi3fmiwl">
          <div className="flex flex-col md:flex-row items-center gap-16" data-id="element-1197" data-mp="1n3ztzh2to444">
            <div className="flex-1" data-id="element-1198" data-mp="1n5u8odni2zzr">
              <div className="w-16 h-16 bg-teal/10 rounded-2xl flex items-center justify-center text-teal mb-6" data-id="element-1199" data-mp="1i56czeobtuz7">
                <Mail size={32} data-id="element-1200" data-mp="1dcvg6kovupav" />
              </div>
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-6" data-id="element-1201" data-mp="1dc3in4bq6hhy">
                Automated Email Engines
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-1202" data-mp="14ejglt61gh6a">
                Nurture leads while you sleep. MISTR builds complex drip
                campaigns and newsletters that drive actual revenue.
              </p>
              <ul className="space-y-4" data-id="element-1203" data-mp="uh5buqth9gtt">
                {['Complex drip campaigns and behavioral automations', 'Weekly newsletter creation and curation', 'Deep list segmentation and personalization', 'A/B testing subject lines and body content', 'Proactive deliverability monitoring'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-1204" data-mp="1knsghsa3la9" data-mpi={i}>
                    <CheckCircle2 size={20} className="text-teal shrink-0 mt-1" data-id="element-1205" data-mp="1ijf798hp9xb" />
                    <span data-id="element-1206" data-mp="bijzoyi3hw33">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="flex-1 w-full" data-id="element-1207" data-mp="gali725uwj1k">
              <div className="bg-dark-bg border border-white/10 rounded-2xl p-6 shadow-2xl flex flex-col gap-3" data-id="element-1208" data-mp="gbd6drx4jeht">
                <div className="bg-white/5 border border-white/10 p-4 rounded-lg flex justify-between items-center" data-id="element-1209" data-mp="3jyn5sztk7cpb">
                  <div data-id="element-1210" data-mp="1phvkrf7l7nms">
                    <div className="text-white font-medium text-sm" data-id="element-1211" data-mp="1pj6ndbpaqpcb">
                      Welcome Series - Day 1
                    </div>
                    <div className="text-steel/70 text-xs mt-1" data-id="element-1212" data-mp="1khhbxuy5w9np">
                      Open Rate: 68%
                    </div>
                  </div>
                  <CheckCircle2 className="text-teal" size={20} data-id="element-1213" data-mp="1uggjfa76xz94" />
                </div>
                <div className="bg-white/5 border border-white/10 p-4 rounded-lg flex justify-between items-center" data-id="element-1214" data-mp="2884v5cdj2lpk">
                  <div data-id="element-1215" data-mp="246jav5ynz55c">
                    <div className="text-white font-medium text-sm" data-id="element-1216" data-mp="2471dqlfu0hm8">
                      Value Add - Day 3
                    </div>
                    <div className="text-steel/70 text-xs mt-1" data-id="element-1217" data-mp="2d893ug872c58">
                      Open Rate: 54%
                    </div>
                  </div>
                  <CheckCircle2 className="text-teal" size={20} data-id="element-1218" data-mp="ntl0f4hl04jc" />
                </div>
                <div className="bg-purple/20 border border-purple/30 p-4 rounded-lg flex justify-between items-center" data-id="element-1219" data-mp="iv9rcvz41y26">
                  <div data-id="element-1220" data-mp="2ui3hodj229l4">
                    <div className="text-white font-medium text-sm" data-id="element-1221" data-mp="2zget2sp00s5g">
                      Soft Pitch - Day 7
                    </div>
                    <div className="text-purple text-xs mt-1 font-bold" data-id="element-1222" data-mp="2kqh0m0k1dgag">
                      A/B Testing Subject Line...
                    </div>
                  </div>
                  <RefreshCw className="text-purple animate-spin" size={20} data-id="element-1223" data-mp="2br3pl3g9kjnm" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* F. Web & Graphic Design */}
      <section className="py-24 px-6 bg-white relative overflow-hidden" data-id="element-1224" data-mp="21tfta930f43c">
        <div className="inner-container max-w-6xl mx-auto" data-id="element-1225" data-mp="26rr5urkovsp9">
          <div className="flex flex-col md:flex-row-reverse items-center gap-16" data-id="element-1226" data-mp="26s95ly6k8p1x">
            <div className="flex-1" data-id="element-1227" data-mp="2kwbdx2gyv1">
              <div className="w-16 h-16 bg-purple/10 rounded-2xl flex items-center justify-center text-purple mb-6" data-id="element-1228" data-mp="3w4uk6t7rb9ko">
                <Palette size={32} data-id="element-1229" data-mp="3ixh8ztcq788" />
              </div>
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-6" data-id="element-1230" data-mp="3ks2kf8jc1cu">
                Visual Identity & Web
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-1231" data-mp="3pq5l1xomi625">
                Your brand needs to look as good as it sounds. MISTR handles the
                visual heavy lifting across all your channels.
              </p>
              <ul className="space-y-4" data-id="element-1232" data-mp="m8dkvw0c2fsv">
                {['Self-building, continuously optimizing website', 'Branded social graphics and carousel creation', 'High-converting ad creatives and variations', 'Consistent visual identity across every touchpoint'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-1233" data-mp="150psax5ug79v" data-mpi={i}>
                    <CheckCircle2 size={20} className="text-purple shrink-0 mt-1" data-id="element-1234" data-mp="109pza2zui9cn" />
                    <span data-id="element-1235" data-mp="2ixj0khe81wk2">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="flex-1 w-full" data-id="element-1236" data-mp="2nvkkt5ndw1x4">
              <div className="grid grid-cols-2 gap-4" data-id="element-1237" data-mp="2rweaa3gh5os4">
                <div className="bg-dark-bg border border-white/10 rounded-2xl p-6 aspect-square flex flex-col items-center justify-center text-center shadow-xl" data-id="element-1238" data-mp="2rvl00sib4h58">
                  <Globe className="text-teal mb-4" size={40} data-id="element-1239" data-mp="31u0sayt2lwgb" />
                  <span className="text-white font-poppins font-semibold" data-id="element-1240" data-mp="36u6ngoyju135">
                    Web Updates
                  </span>
                  <span className="text-steel text-xs mt-2" data-id="element-1241" data-mp="3bkn40hxo22tq">
                    Deployed 2 hrs ago
                  </span>
                </div>
                <div className="bg-dark-bg border border-white/10 rounded-2xl p-6 aspect-square flex flex-col items-center justify-center text-center shadow-xl" data-id="element-1242" data-mp="3fokyugfykrgz">
                  <Layers className="text-purple mb-4" size={40} data-id="element-1243" data-mp="3plfyj2nywxwu" />
                  <span className="text-white font-poppins font-semibold" data-id="element-1244" data-mp="30h91pbf0qzby">
                    Ad Creatives
                  </span>
                  <span className="text-steel text-xs mt-2" data-id="element-1245" data-mp="2vhmkkqw2kyxv">
                    12 variants generated
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. STRATEGY SECTION */}
      <section className="py-24 px-6 bg-white relative overflow-hidden border-t border-gray-100" data-id="element-1246" data-mp="2hqripyjzni3s">
        <div className="inner-container max-w-7xl mx-auto" data-id="element-1247" data-mp="2cs6kg4oar8hz">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1248" data-mp="2ctrbhkbek9b3">
            <span className="text-teal text-sm font-poppins font-bold uppercase tracking-widest block mb-4" data-id="element-1249" data-mp="2qkcy9k3z93e9">
              The Masterplan
            </span>
            <h2 className="font-poppins font-extrabold text-4xl md:text-5xl leading-tight text-gray-900 mb-6" data-id="element-1250" data-mp="1y30nkncxlud9">
              Real Strategies. <br className="hidden md:block" data-id="element-1251" data-mp="1u28djrw8jlfx" /> Not Just
              Automation.
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1252" data-mp="1jx8e7ddgj7vz">
              Automation without strategy is just making mistakes faster. MISTR
              executes proven, high-level marketing playbooks.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-1253" data-mp="1a0ncb0wqicwd">
            {[{
            icon: RefreshCw,
            title: 'The Content Flywheel',
            desc: 'MISTR writes a core blog post, breaks it into 5 social posts, drafts an email newsletter about it, and creates retargeting ads for readers. One idea, infinite mileage.',
            color: 'text-teal',
            bg: 'bg-teal/10'
          }, {
            icon: Globe,
            title: 'The Omnipresence Play',
            desc: 'Be everywhere your customer looks. MISTR ensures your messaging is perfectly synced across Search, Social, Email, and your Website simultaneously.',
            color: 'text-purple',
            bg: 'bg-purple/10'
          }, {
            icon: Target,
            title: 'The Trust Ladder',
            desc: 'Cold traffic needs different messaging than warm leads. MISTR automatically moves prospects from Awareness → Value → Authority → Conversion.',
            color: 'text-teal',
            bg: 'bg-teal/10'
          }, {
            icon: Database,
            title: 'The Data Loop',
            desc: 'Every click, open, and conversion feeds back into the Persona Engine. MISTR learns what works and automatically adjusts future campaigns to match.',
            color: 'text-purple',
            bg: 'bg-purple/10'
          }].map((strategy, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-dark-bg border border-white/10 rounded-2xl p-8 shadow-xl hover:shadow-2xl hover:border-purple/30 transition-all group" data-id="element-1254" data-mp="354ior3od7r6z" data-mpi={i}>
                <div className={`w-14 h-14 ${strategy.bg} rounded-xl flex items-center justify-center ${strategy.color} mb-6 group-hover:scale-110 transition-transform`} data-id="element-1255" data-mp="1q6edz5skrw6u">
                  <strategy.icon size={28} data-id="element-1256" data-mp="xeus1e4lg415" />
                </div>
                <h3 className="font-poppins font-bold text-2xl text-white mb-3" data-id="element-1257" data-mp="1c6lt3r3qca6s">
                  {strategy.title}
                </h3>
                <p className="text-steel leading-relaxed" data-id="element-1258" data-mp="17d0upxy85e72">{strategy.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 6. SELF-EVOLVING PERSONA */}
      <section className="py-24 px-6 bg-white relative overflow-hidden border-t border-gray-100" data-id="element-1259" data-mp="eobwq7sgtozj">
        <div className="inner-container max-w-5xl mx-auto" data-id="element-1260" data-mp="2eql7hxn85vjj">
          <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-1261" data-mp="2jqhfe7z1m74d">
            <h2 className="font-poppins font-extrabold text-4xl md:text-5xl leading-tight text-gray-900 mb-6" data-id="element-1262" data-mp="2oir2lg8rwt2y">
              A Persona That Grows With You
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1263" data-mp="2slx3s9h13u2k">
              Static brand guides collect dust. When your business pivots,
              launches a new product, or shifts tone, MISTR adapts
              automatically. No manual updates required.
            </p>
          </div>

          <div className="relative" data-id="element-1264" data-mp="24t6t03kv3gtv">
            {/* Timeline Line */}
            <div className="absolute left-[27px] md:left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-purple via-teal to-purple md:-translate-x-1/2 opacity-50" data-id="element-1265" data-mp="29unrjsfvir7a" />

            <div className="space-y-12" data-id="element-1266" data-mp="3l8ron7hrcvgk">
              {[{
              time: 'Day 1',
              title: 'The Foundation',
              desc: 'MISTR ingests your historical data (Zoom, Slack, Emails) to establish your baseline voice, core offers, and target audience.',
              align: 'right'
            }, {
              time: 'Month 1',
              title: 'The Refinement',
              desc: 'By analyzing which social posts and emails get the highest engagement, MISTR subtly tweaks its tone to maximize resonance.',
              align: 'left'
            }, {
              time: 'Month 6',
              title: 'The Pivot',
              desc: 'You launch a new service. MISTR automatically reads the new sales collateral and instantly updates all marketing channels to reflect the new offering.',
              align: 'right'
            }, {
              time: 'Year 1',
              title: 'The Mastermind',
              desc: 'MISTR now knows your customers better than anyone. It anticipates market trends and suggests new marketing angles based on a year of deep data.',
              align: 'left'
            }].map((step, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className={`relative flex flex-col md:flex-row items-start md:items-center gap-8 ${step.align === 'left' ? 'md:flex-row-reverse' : ''}`} data-id="element-1267" data-mp="uwj8jrk1zjuh" data-mpi={i}>
                  {/* Timeline Dot */}
                  <div className="absolute left-0 md:left-1/2 w-14 h-14 bg-dark-bg border-4 border-purple rounded-full flex items-center justify-center md:-translate-x-1/2 z-10 shadow-[0_0_20px_rgba(124,58,237,0.4)]" data-id="element-1268" data-mp="295jsgqidulg">
                    <Clock size={20} className="text-white" data-id="element-1269" data-mp="3jqszqinqztbj" />
                  </div>

                  <div className={`ml-20 md:ml-0 md:w-1/2 ${step.align === 'left' ? 'md:pr-16 md:text-right' : 'md:pl-16'}`} data-id="element-1270" data-mp="3et169k7gfz58">
                    <div className="text-teal font-bold text-sm uppercase tracking-wider mb-2" data-id="element-1271" data-mp="3sq6bxj39avzy">
                      {step.time}
                    </div>
                    <h3 className="font-poppins font-bold text-2xl text-gray-900 mb-3" data-id="element-1272" data-mp="6k10yyovb9ol">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed" data-id="element-1273" data-mp="1k54dkii2kj2">{step.desc}</p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* 7. FINAL CTA */}
      <section className="relative py-32 px-6 bg-hero-gradient overflow-hidden flex items-center justify-center text-center border-t border-white/10" data-id="element-1274" data-mp="pasgkhzg7wbf">
        <div className="absolute inset-0 pointer-events-none" data-id="element-1275" data-mp="kae7qxq0wicc">
          <motion.div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple/30 rounded-full blur-[100px]" animate={{
          x: [0, 50, 0],
          y: [0, 30, 0]
        }} transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1276" data-mp="3e5ia31rh32w7" />
          <motion.div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal/20 rounded-full blur-[100px]" animate={{
          x: [0, -40, 0],
          y: [0, -50, 0]
        }} transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1277" data-mp="pswl7mv3093" />
        </div>

        <div className="inner-container relative z-10 max-w-4xl" data-id="element-1278" data-mp="emzbus8dmgtk">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-1279" data-mp="2cgo5h3ocgjbk">
            <h2 className="font-poppins font-extrabold text-5xl md:text-6xl mb-6 leading-tight text-white" data-id="element-1280" data-mp="2467a2ojl4ksj">
              Put Your Marketing <br data-id="element-1281" data-mp="1z7w0mex7ccau" />
              <span className="text-gradient" data-id="element-1282" data-mp="1z59y4tp64mzh">On Autopilot.</span>
            </h2>
            <p className="text-xl text-steel mb-10" data-id="element-1283" data-mp="1v7c3hbcmp8op">
              One AI. Every channel. Your exact voice. Always on.
            </p>

            <button className="btn-primary text-lg px-10 py-5 mb-12 shadow-[0_0_40px_rgba(124,58,237,0.4)] hover:shadow-[0_0_60px_rgba(124,58,237,0.6)] transition-shadow flex items-center gap-2 mx-auto" data-id="element-1284" data-mp="2mwxdqfgzcbmg">
              Book a Strategy Call <ArrowRight size={20} data-id="element-1285" data-mp="2dx1zu2w1i2iq" />
            </button>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm" data-id="element-1286" data-mp="2dylfbr9vg9jg">
              <div data-id="element-1287" data-mp="12gu5goqrhxi5">
                <div className="text-2xl font-bold text-white font-poppins mb-1" data-id="element-1288" data-mp="12gan56x7973l">
                  100%
                </div>
                <div className="text-[10px] md:text-xs text-steel uppercase tracking-wider font-medium" data-id="element-1289" data-mp="32lfvgjzwa0bi">
                  Automated
                </div>
              </div>
              <div data-id="element-1290" data-mp="2smgulnldf8z2">
                <div className="text-2xl font-bold text-white font-poppins mb-1" data-id="element-1291" data-mp="2sn020adeaeyu">
                  Local
                </div>
                <div className="text-[10px] md:text-xs text-steel uppercase tracking-wider font-medium" data-id="element-1292" data-mp="3l9uiyxb06w7k">
                  Data Privacy
                </div>
              </div>
              <div data-id="element-1293" data-mp="3qb0e11s4yvk9">
                <div className="text-2xl font-bold text-white font-poppins mb-1" data-id="element-1294" data-mp="3bk19c1uf8r5y">
                  8+
                </div>
                <div className="text-[10px] md:text-xs text-steel uppercase tracking-wider font-medium" data-id="element-1295" data-mp="3gickqh0d79qa">
                  Channels
                </div>
              </div>
              <div data-id="element-1296" data-mp="cu156q823yu7">
                <div className="text-2xl font-bold text-white font-poppins mb-1" data-id="element-1297" data-mp="cuj6i19kt38z">
                  24/7
                </div>
                <div className="text-[10px] md:text-xs text-steel uppercase tracking-wider font-medium" data-id="element-1298" data-mp="1xj55esrz9548">
                  Execution
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </main>;
}